// Unified event tracking — batched, sendBeacon-based, short keys to bypass ad blockers
//
// Two tiers of tracking:
//   1. Aggregate (pv, fn, ck) — consent-free under CNIL first-party exemption.
//      No device storage, no per-user profiling, data is aggregated server-side.
//   2. Per-user (uc, vw) — requires statistics consent (cookie banner).
//      Writes to per-user Firestore collections (user_activity, user_watch_sessions).
import { logEvent } from 'firebase/analytics';
import { auth } from '../firebase';
import { getAnalyticsInstance } from '../firebase';
import { API_BASE_URL } from '../config/runtime';
import { hasAnalyticsConsent } from './consent';

const ga4 = (eventName, params) => {
    if (!hasAnalyticsConsent()) return;
    const a = getAnalyticsInstance();
    if (a) logEvent(a, eventName, params);
};

const GA4_FUNNEL_MAP = {
    type: 'topic_typed',
    submit: 'generate_video',
    watch: 'video_watch',
    signup: 'sign_up',
};

const SESSION_ID = crypto.randomUUID();
const fired = new Set();
const queue = [];
let flushTimer = null;

const flush = () => {
    if (queue.length === 0) return;
    const batch = queue.splice(0);
    const blob = new Blob([JSON.stringify({ ev: batch })], { type: 'application/json' });

    if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API_BASE_URL}/api/ev/b`, blob);
    } else {
        fetch(`${API_BASE_URL}/api/ev/b`, { method: 'POST', body: blob, keepalive: true }).catch(() => {});
    }
};

const scheduleFlush = () => {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
        flushTimer = null;
        flush();
    }, 5000);
};

// Flush queued events when page hides or unloads
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush();
    });
    window.addEventListener('beforeunload', flush);
}

// THE HEARTBEAT — how long somebody was here, and how many were here at once.
//
// Neither question could be answered before. The server stored two timestamps per visitor per
// day, first seen and last seen, and measured over 461 real visitor-days that proxy fails in
// both directions at the same time: 54% of visitors record a span of exactly ZERO, because
// last_seen only advances on a page view and someone who lands and reads for ten minutes never
// sends another; and of the rest the median span is TWENTY HOURS, because the same address
// arriving morning and evening looks like one unbroken session. Peak concurrency bounded from
// that came out "somewhere between 13 and 72 for the week".
//
// VISIBLE TIME, NOT WALL TIME. The page measures its own foregrounded milliseconds and reports
// the delta. A tab left open overnight contributes nothing, which no amount of server-side
// arithmetic over beat arrivals could distinguish from someone reading.
//
// EVERY 45s, AND SHORTER THAN THE BUCKET ON PURPOSE. Presence is recorded in one-minute slots,
// so a beat interval below 60s means two consecutive beats can never skip a slot entirely and
// a visitor who stays cannot vanish from the middle of their own session. Equal values would
// let ordinary timer jitter drop one.
//
// THE INTERVAL IS THE COST. Each beat is one Firestore write and — by construction — zero
// reads. At the 5,000 concurrent visitors the traffic gate is set to, a minute of beats is
// ~6,700 writes; the same design with a read-first check would be millions of READS a day,
// more than the entire monthly bill this is supposed to help size.
const HEARTBEAT_MS = 45_000;
let visibleSince = (typeof document !== 'undefined'
                    && document.visibilityState === 'visible') ? Date.now() : null;
let unreported = 0;

/** Fold the time since the last check into the pending total. Idempotent. */
const takeVisibleMs = () => {
    if (visibleSince != null) {
        unreported += Date.now() - visibleSince;
        visibleSince = Date.now();
    }
    const ms = Math.round(unreported);
    unreported = 0;
    return ms;
};

const beat = () => {
    const ms = takeVisibleMs();
    // A beat with no time on it still marks PRESENCE — which is the half that answers peak
    // concurrency — so it is sent even when the page has been hidden throughout.
    queue.push({ t: 'hb', ms });
    scheduleFlush();
};

if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            // Send it NOW, before clearing the clock. A hidden tab may never run another
            // timer, and on mobile "hidden" is usually "closed" — a beat deferred here is one
            // that is never sent, which is exactly the short session we most want to measure.
            // `beat()` reads `visibleSince`, so it has to run before that is nulled.
            beat();
            flush();
            visibleSince = null;
        } else {
            visibleSince = Date.now();
        }
    });
    // The last beat of the page's life, on the event that actually fires on mobile.
    window.addEventListener('pagehide', () => { beat(); flush(); });
    setInterval(() => {
        if (document.visibilityState === 'visible') beat();
    }, HEARTBEAT_MS);
}

// Aggregate events — no consent required (first-party, no device storage)
const enqueue = (event) => {
    queue.push(event);
    scheduleFlush();
};

// Per-user events — requires analytics consent
const enqueueConsented = (event) => {
    if (!hasAnalyticsConsent()) return;
    queue.push(event);
    scheduleFlush();
};

// Track a conversion funnel event (visit, type, submit, watch, signup)
// Deduped per session on client side; server also deduplicates
export const trackFunnelEvent = (event) => {
    if (fired.has(event)) return;
    fired.add(event);

    // Use Firebase anonymous UID for server-side dedup (no sessionStorage)
    const sid = auth.currentUser?.uid || SESSION_ID;
    enqueue({ t: 'fn', n: event, sid, e: auth.currentUser?.email || null });

    // Mirror to GA4 (consent-gated inside ga4())
    const ga4Event = GA4_FUNNEL_MAP[event];
    if (ga4Event) ga4(ga4Event, { method: 'organic' });
};

// Track a pageview — called by useEv hook on route change
export const trackPageview = ({ path, isNewSession, userId, userEmail, referrer }) => {
    enqueue({
        t: 'pv',
        p: path,
        ns: isNewSession || false,
        uid: userId || null,
        e: userEmail || null,
        ref: referrer || null,
    });

    // Mirror to GA4
    ga4('page_view', { page_path: path });
};

// Track a button click — fire-and-forget via existing batch queue
export const trackClick = (buttonId, page) => {
    // Aggregate click — consent-free
    enqueue({
        t: 'ck',
        b: buttonId,
        p: page,
        uid: auth.currentUser?.uid || null,
        e: auth.currentUser?.email || null,
    });

    // Per-user click tracking — requires consent
    const uid = auth.currentUser?.uid;
    if (uid) {
        enqueueConsented({ t: 'uc', b: buttonId, uid });
    }

    ga4('button_click', { button_id: buttonId, page_path: page });
};

// Track video watch progress — called by VideoPlayer heartbeat
// Per-user tracking — requires consent
export const trackVideoWatch = ({ videoId, watchedSeconds, videoDuration, maxPosition, completed }) => {
    if (!videoId) return;
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    enqueueConsented({
        t: 'vw',
        vid: videoId,
        uid,
        ws: Math.round(watchedSeconds),
        vd: Math.round(videoDuration),
        mp: Math.round(maxPosition),
        done: completed || false,
    });
};
